import type { FeatDefinition } from '@character-creator/types';

/**
 * Ignus Feats
 *
 * In Ignus, feats work differently with a tiered system:
 * - Option 1: +1 stat + tier-3 ability
 * - Option 2: tier-2 ability
 * - Option 3: -1 stat + tier-1 ability (powerful)
 *
 * Tier-1: Big, game-changing abilities (e.g., Barbarian casting while raging)
 * Tier-2: Add traits/skills from other classes (e.g., metamagic, channel divinity)
 * Tier-3: Add skills, proficiencies, or improve existing traits (e.g., +10 speed)
 *
 * The feats below are specific named feats from the Ignus source document.
 */
export const ignusFeats: FeatDefinition[] = [
  {
    id: 'grappler',
    name: 'Grappler',
    description:
      "You've developed the skills necessary to hold your own in close-quarters grappling. You gain the following benefits:",
    prerequisites: {
      abilityScores: {
        strength: 13,
      },
    },
    benefits: [
      'You have advantage on attack rolls against a creature you are grappling.',
      'You can use your action to try to pin a creature grappled by you. To do so, make another grapple check. If you succeed, you and the creature are both restrained until the grapple ends.',
    ],
    source: 'ignus',
  },
  {
    id: 'two-fisted-spellcasting',
    name: 'Two-Fisted Spellcasting',
    description:
      "You've spent years forging your mind into a steel trap and are now able to hold onto two concentration spells at once. However, this requires immense mental focus.",
    prerequisites: {
      abilityScores: {
        intelligence: 13,
      },
    },
    benefits: [
      'You can concentrate on two spells at once.',
      'When concentrating on two spells, you have a -4 penalty to your Dexterity score.',
    ],
    source: 'ignus',
  },
  {
    id: 'arcane-strategist',
    name: 'Arcane Strategist',
    description:
      'Your ability to quickly analyze a situation and make strategic choices on the fly grants you several benefits:',
    prerequisites: {
      abilityScores: {
        intelligence: 13,
      },
    },
    benefits: [
      'You make saving throws to maintain Concentration on a spell you have cast at advantage.',
      'When you roll Initiative, you can add your Intelligence modifier to the roll instead of Dexterity.',
    ],
    source: 'ignus',
  },
  {
    id: 'one-man-army',
    name: 'One-Man Army',
    description:
      "You've mastered the art of fighting against unfair odds and are able to twist and redirect damage with startling alacrity in a fight.",
    prerequisites: {
      abilityScores: {
        dexterity: 12,
      },
    },
    abilityScoreIncrease: {
      choices: ['dexterity'],
      amount: 1,
    },
    benefits: [
      'Increase your Dexterity by 1, to a maximum of 20.',
      'When you drop a creature to zero hit points with a melee attack, any remaining damage beyond what it took to get that creature to zero can be transferred to another creature within range. That creature must make a Dexterity saving throw (DC = 10 + your Dexterity modifier) or take the remaining damage. If the creature saves, they take half of the remaining damage.',
    ],
    source: 'ignus',
  },
];
